import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  API_URL, 
  INITIAL_FORM_DATA, 
  INITIAL_ASSIGN_DATA, 
  INITIAL_TRUCK_DATA 
} from '../utils/constants';
import { prepareTruckDataForApi, initTruckDataFromTruck } from '../utils/helpers';

/**
 * Hook principal para gestionar el estado y operaciones de Despachos
 * @param {Object} currentUser - Usuario actual para filtrar despachos según rol
 */
export const useDispatches = (currentUser) => {
  // Data state
  const [dispatches, setDispatches] = useState([]);
  const [importadoras, setImportadoras] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Verificar si es transportista
  const isTransportista = currentUser?.role === 'TRANSPORTISTA';

  // UI state - Set para permitir múltiples despachos expandidos
  const [expandedDispatches, setExpandedDispatches] = useState(new Set());
  
  // Filtros state
  const [filters, setFilters] = useState({
    loadDate: '',
    deliveryDate: '',
    dispatchNumber: '',
    importadoraId: '',
    broker: '',
    destination: '',
    status: ''
  });
  
  // Modal state
  const [modals, setModals] = useState({
    create: false,
    assign: false,
    truckData: false,
    edit: false,
    status: false,
    editDispatch: false,
    editTruckAssignment: false
  });

  // Selection state
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [isModification, setIsModification] = useState(false);

  // Form state
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [assignData, setAssignData] = useState(INITIAL_ASSIGN_DATA);
  const [truckData, setTruckData] = useState(INITIAL_TRUCK_DATA);
  const [editData, setEditData] = useState({ status: '' });
  const [justification, setJustification] = useState('');
  const [dispatchNumberError, setDispatchNumberError] = useState('');
  const [assignmentData, setAssignmentData] = useState(INITIAL_ASSIGN_DATA);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [dispatchesRes, importadorasRes, companiesRes, trucksRes, trailersRes, driversRes, businessesRes, comprasRes] = await Promise.all([
        axios.get(`${API_URL}/dispatches`),
        axios.get(`${API_URL}/importadoras`),
        axios.get(`${API_URL}/companies`),
        axios.get(`${API_URL}/trucks`),
        axios.get(`${API_URL}/trailers`),
        axios.get(`${API_URL}/drivers`),
        axios.get(`${API_URL}/businesses`),
        axios.get(`${API_URL}/compras`)
      ]);
      
      let dispatchList = dispatchesRes.data;
      let companyList = companiesRes.data;
      
      // Si es TRANSPORTISTA, filtrar despachos donde tiene camiones asignados
      if (isTransportista && currentUser?.id) {
        // Obtener IDs de empresas del transportista
        const myCompanyIds = companyList
          .filter(c => c.transportista_id === currentUser.id)
          .map(c => c.id);
        
        // Filtrar despachos que tengan al menos un camión de sus empresas
        dispatchList = dispatchList.filter(dispatch => {
          const dispatchTrucks = dispatch.trucks || [];
          return dispatchTrucks.some(truck => myCompanyIds.includes(truck.company_id));
        });
      }
      
      setDispatches(dispatchList);
      setImportadoras(importadorasRes.data);
      setCompanies(companyList);
      setTrucks(trucksRes.data);
      setTrailers(trailersRes.data);
      setDrivers(driversRes.data);
      setBusinesses(businessesRes.data);
      setCompras(comprasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [isTransportista, currentUser?.id]);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [fetchData, currentUser]);

  // Filtrar despachos según los filtros activos
  const filteredDispatches = useMemo(() => {
    return dispatches.filter(d => {
      // Filtro por fecha de carga (puede estar en despacho o en camiones)
      if (filters.loadDate) {
        const dispatchLoadDate = d.load_date || '';
        const trucksHaveDate = d.trucks?.some(t => t.load_date === filters.loadDate);
        if (dispatchLoadDate !== filters.loadDate && !trucksHaveDate) return false;
      }
      // Filtro por fecha de descarga
      if (filters.deliveryDate) {
        const dispatchDeliveryDate = d.delivery_date || '';
        const trucksHaveDate = d.trucks?.some(t => t.delivery_date === filters.deliveryDate);
        if (dispatchDeliveryDate !== filters.deliveryDate && !trucksHaveDate) return false;
      }
      // Filtro por número de despacho
      if (filters.dispatchNumber && !d.dispatch_number?.toLowerCase().includes(filters.dispatchNumber.toLowerCase())) return false;
      // Filtro por importadora
      if (filters.importadoraId && d.importadora_id !== filters.importadoraId) return false;
      // Filtro por corredor (puede estar en despacho o en camiones)
      if (filters.broker) {
        const dispatchBroker = d.broker || '';
        const trucksHaveBroker = d.trucks?.some(t => t.broker === filters.broker);
        if (dispatchBroker !== filters.broker && !trucksHaveBroker) return false;
      }
      // Filtro por destino (en camiones)
      if (filters.destination) {
        const trucksHaveDestination = d.trucks?.some(t => 
          t.destination?.toLowerCase().includes(filters.destination.toLowerCase())
        );
        if (!trucksHaveDestination) return false;
      }
      // Filtro por estado
      if (filters.status && d.status !== filters.status) return false;
      return true;
    });
  }, [dispatches, filters]);

  // Limpiar filtros
  const clearFilters = useCallback(() => {
    setFilters({
      loadDate: '',
      deliveryDate: '',
      dispatchNumber: '',
      importadoraId: '',
      broker: '',
      destination: '',
      status: ''
    });
  }, []);

  // Verificar si hay filtros activos
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(v => v !== '');
  }, [filters]);

  // Reset all forms
  const resetForms = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setAssignData(INITIAL_ASSIGN_DATA);
    setTruckData(INITIAL_TRUCK_DATA);
    setEditData({ status: '' });
    setJustification('');
    setSelectedDispatch(null);
    setSelectedTruck(null);
    setDispatchNumberError('');
    setIsModification(false);
    setAssignmentData(INITIAL_ASSIGN_DATA);
  }, []);

  // Modal helpers
  const openModal = useCallback((modalName) => {
    setModals(prev => ({ ...prev, [modalName]: true }));
  }, []);

  const closeModal = useCallback((modalName) => {
    setModals(prev => ({ ...prev, [modalName]: false }));
    resetForms();
  }, [resetForms]);

  // Validation
  const validateDispatchNumber = useCallback((number) => {
    const exists = dispatches.some(d => d.dispatch_number === number.toUpperCase());
    if (exists) {
      setDispatchNumberError('Este N° de despacho ya existe');
      return false;
    }
    setDispatchNumberError('');
    return true;
  }, [dispatches]);

  // API Operations
  const createDispatch = useCallback(async (e) => {
    e.preventDefault();
    if (!validateDispatchNumber(formData.dispatch_number)) {
      toast.error('El N° de despacho ya existe');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/dispatches`, {
        ...formData,
        dispatch_number: formData.dispatch_number.toUpperCase(),
        crt_number: formData.crt_number.toUpperCase()
      });
      toast.success('Despacho creado exitosamente');
      closeModal('create');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear despacho');
    }
  }, [formData, validateDispatchNumber, closeModal, fetchData]);

  const assignTruck = useCallback(async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/dispatches/${selectedDispatch.id}/assign-truck`, {
        company_id: assignData.company_id,
        truck_id: assignData.truck_id,
        trailer_id: assignData.trailer_id || null,
        driver_id: assignData.driver_id,
        negocio_id: assignData.negocio_id || null,
        compra_id: assignData.compra_id || null
      });
      toast.success('Camión asignado exitosamente');
      closeModal('assign');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar camión');
    }
  }, [selectedDispatch, assignData, closeModal, fetchData]);

  const updateTruckData = useCallback(async (e) => {
    e.preventDefault();
    
    if (isModification && !justification.trim()) {
      toast.error('La justificación es obligatoria para modificar datos existentes');
      return;
    }
    
    try {
      const params = new URLSearchParams();
      if (isModification) {
        params.append('justification', justification.toUpperCase());
        params.append('is_modification', 'true');
      } else {
        params.append('is_modification', 'false');
      }
      
      await axios.put(
        `${API_URL}/dispatches/${selectedDispatch.id}/trucks/${selectedTruck.id}?${params.toString()}`,
        prepareTruckDataForApi(truckData)
      );
      toast.success(isModification ? 'Datos del camión modificados' : 'Datos del camión agregados');
      closeModal('truckData');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar datos');
    }
  }, [selectedDispatch, selectedTruck, truckData, isModification, justification, closeModal, fetchData]);

  const changeStatus = useCallback(async (e) => {
    e.preventDefault();
    
    const currentStatus = selectedDispatch?.status;
    const newStatus = editData.status;
    
    if (currentStatus === 'Finalizado' && newStatus === 'En Proceso' && !justification.trim()) {
      toast.error('Se requiere justificación para reabrir un despacho finalizado');
      return;
    }
    
    try {
      const params = new URLSearchParams();
      params.append('new_status', newStatus);
      if (justification.trim()) {
        params.append('justification', justification.toUpperCase());
      }
      
      await axios.put(`${API_URL}/dispatches/${selectedDispatch.id}/status?${params.toString()}`);
      toast.success(`Estado cambiado a ${newStatus}`);
      closeModal('status');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  }, [selectedDispatch, editData, justification, closeModal, fetchData]);

  const updateDispatch = useCallback(async (e) => {
    e.preventDefault();
    if (!justification.trim()) {
      toast.error('La justificación es obligatoria');
      return;
    }
    
    try {
      await axios.put(`${API_URL}/dispatches/${selectedDispatch.id}`, {
        data: {
          importadora_id: formData.importadora_id,
          dispatch_number: formData.dispatch_number.toUpperCase(),
          crt_number: formData.crt_number.toUpperCase(),
          truck_count: formData.truck_count
        },
        justification: justification.toUpperCase()
      });
      toast.success('Despacho actualizado');
      closeModal('editDispatch');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  }, [selectedDispatch, formData, justification, closeModal, fetchData]);

  const deleteDispatch = useCallback(async (dispatchId, dispatchNumber) => {
    if (!window.confirm(`¿Estás seguro de eliminar el despacho ${dispatchNumber}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/dispatches/${dispatchId}`);
      toast.success('Despacho eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  }, [fetchData]);

  const removeTruck = useCallback(async (dispatchId, truckId) => {
    const reason = window.prompt('Justificación para eliminar este camión:');
    if (!reason) return;
    
    try {
      await axios.delete(`${API_URL}/dispatches/${dispatchId}/trucks/${truckId}?justification=${encodeURIComponent(reason.toUpperCase())}`);
      toast.success('Camión eliminado del despacho');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  }, [fetchData]);

  // Función para ADMIN: actualizar asignación (transporte, patente, chofer)
  const updateTruckAssignment = useCallback(async (e) => {
    e.preventDefault();
    
    try {
      // Obtener nombres de los nuevos valores
      const company = companies.find(c => c.id === assignmentData.company_id);
      const truck = trucks.find(t => t.id === assignmentData.truck_id);
      const driver = drivers.find(d => d.id === assignmentData.driver_id);
      const trailer = trailers.find(t => t.id === assignmentData.trailer_id);
      
      const updatePayload = {
        company_id: assignmentData.company_id,
        company_name: company?.name || '',
        truck_id: assignmentData.truck_id,
        license_plate: truck?.license_plate || '',
        driver_id: assignmentData.driver_id,
        driver_name: driver?.name || '',
        trailer_id: assignmentData.trailer_id === 'none' ? null : (assignmentData.trailer_id || null),
        trailer_plate: trailer?.license_plate || null
      };
      
      await axios.put(
        `${API_URL}/dispatches/${selectedDispatch.id}/trucks/${selectedTruck.id}/assignment`,
        updatePayload
      );
      
      toast.success('Asignación actualizada correctamente');
      closeModal('editTruckAssignment');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar asignación');
    }
  }, [selectedDispatch, selectedTruck, assignmentData, companies, trucks, drivers, trailers, closeModal, fetchData]);

  // Modal openers with data initialization
  const openAssignModal = useCallback((dispatch) => {
    setSelectedDispatch(dispatch);
    setAssignData(INITIAL_ASSIGN_DATA);
    openModal('assign');
  }, [openModal]);

  const openAddTruckDataModal = useCallback((dispatch, truck) => {
    setSelectedDispatch(dispatch);
    setSelectedTruck(truck);
    setIsModification(false);
    // Pre-llenar con valores del despacho si la línea no tiene datos
    const initialData = initTruckDataFromTruck(truck);
    // Valores recomendados del despacho
    if (!truck.load_date && dispatch.load_date) initialData.load_date = dispatch.load_date;
    if (!truck.delivery_date && dispatch.delivery_date) initialData.delivery_date = dispatch.delivery_date;
    if (!truck.broker && dispatch.broker) initialData.broker = dispatch.broker;
    if (!truck.freight_value && dispatch.freight_value_per_ton) initialData.freight_value = dispatch.freight_value_per_ton;
    // Pre-llenar KG cargados con 28500 si no tiene valor
    if (!truck.kg_loaded) initialData.kg_loaded = 28500;
    setTruckData(initialData);
    openModal('truckData');
  }, [openModal]);

  const openModifyTruckDataModal = useCallback((dispatch, truck) => {
    setSelectedDispatch(dispatch);
    setSelectedTruck(truck);
    setIsModification(true);
    setTruckData(initTruckDataFromTruck(truck));
    openModal('truckData');
  }, [openModal]);

  const openStatusModal = useCallback((dispatch) => {
    setSelectedDispatch(dispatch);
    setEditData({ status: dispatch.status });
    setJustification('');
    openModal('status');
  }, [openModal]);

  const openEditDispatchModal = useCallback((dispatch) => {
    setSelectedDispatch(dispatch);
    setFormData({
      importadora_id: dispatch.importadora_id || '',
      dispatch_number: dispatch.dispatch_number || '',
      crt_number: dispatch.crt_number || '',
      truck_count: dispatch.truck_count || 1,
      status: dispatch.status || 'Pendiente'
    });
    setJustification('');
    openModal('editDispatch');
  }, [openModal]);

  // Abrir modal para ADMIN editar asignación (transporte, patente, chofer)
  const openEditTruckAssignmentModal = useCallback((dispatch, truck) => {
    setSelectedDispatch(dispatch);
    setSelectedTruck(truck);
    setAssignmentData({
      company_id: truck.company_id || '',
      truck_id: truck.truck_id || '',
      trailer_id: truck.trailer_id || 'none',
      driver_id: truck.driver_id || ''
    });
    openModal('editTruckAssignment');
  }, [openModal]);

  // Toggle dispatch expansion - independiente para cada despacho
  const toggleDispatch = useCallback((dispatchId) => {
    setExpandedDispatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dispatchId)) {
        newSet.delete(dispatchId);
      } else {
        newSet.add(dispatchId);
      }
      return newSet;
    });
  }, []);
  
  // Helper para verificar si un despacho está expandido
  const isDispatchExpanded = useCallback((dispatchId) => {
    return expandedDispatches.has(dispatchId);
  }, [expandedDispatches]);

  // Get importadora name helper
  const getImportadoraName = useCallback((id) => {
    const imp = importadoras.find(i => i.id === id);
    return imp ? (imp.alias || imp.razon_social) : '';
  }, [importadoras]);

  // Filter helpers for selects
  const getFilteredTrucks = useCallback((companyId) => {
    return companyId ? trucks.filter(t => t.company_id === companyId) : [];
  }, [trucks]);

  const getFilteredTrailers = useCallback((companyId) => {
    return companyId ? trailers.filter(t => t.company_id === companyId) : [];
  }, [trailers]);

  const getFilteredDrivers = useCallback((companyId) => {
    return companyId ? drivers.filter(d => d.company_id === companyId) : [];
  }, [drivers]);

  // Reordenar despachos (drag & drop) - Ahora persiste en la base de datos
  const reorderDispatches = useCallback(async (newOrder) => {
    // Actualiza el orden local inmediatamente (optimistic update)
    setDispatches(newOrder);
    
    // Guardar orden en backend
    try {
      const orderIds = newOrder.map(d => d.id);
      await axios.post(`${API_URL}/dispatches/reordenar`, { dispatch_ids: orderIds });
      // También guardamos en localStorage como fallback
      localStorage.setItem('dispatchesOrder', JSON.stringify(orderIds));
    } catch (error) {
      console.error('Error guardando orden:', error);
      toast.error('Error al guardar el orden. Se restaurará al recargar.');
    }
  }, []);

  // Reordenar camiones dentro de un despacho (drag & drop) - Persiste en la base de datos
  const reorderTrucks = useCallback(async (dispatchId, updatedTrucks) => {
    // Optimistic update: actualizar el despacho localmente
    setDispatches(prev => prev.map(d => {
      if (d.id === dispatchId) {
        return { ...d, trucks: updatedTrucks };
      }
      return d;
    }));

    // Persistir en backend
    try {
      const truckIds = updatedTrucks.map(t => t.id);
      await axios.put(`${API_URL}/dispatches/${dispatchId}/trucks/reorder`, { truck_ids: truckIds });
    } catch (error) {
      console.error('Error guardando orden de camiones:', error);
      toast.error('Error al guardar el orden de camiones.');
      fetchData(); // Revertir al estado del servidor
    }
  }, [fetchData]);

  // ============================================
  // PESAJES DE DESCARGA
  // ============================================
  
  // Subir pesaje de descarga
  const uploadPesaje = useCallback(async (dispatchId, truckId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API_URL}/dispatches/${dispatchId}/trucks/${truckId}/pesaje`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Pesaje subido correctamente');
      fetchData();
    } catch (error) {
      console.error('Error subiendo pesaje:', error);
      toast.error(error.response?.data?.detail || 'Error al subir pesaje');
    }
  }, [fetchData]);

  // Ver pesaje de descarga
  const viewPesaje = useCallback((pesajeUrl) => {
    // Abrir en nueva pestaña con el prefijo /api
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}/api${pesajeUrl}`;
    window.open(fullUrl, '_blank');
  }, []);

  // Eliminar pesaje de descarga
  const deletePesaje = useCallback(async (dispatchId, truckId) => {
    if (!window.confirm('¿Eliminar el archivo de pesaje?')) return;
    
    try {
      await axios.delete(`${API_URL}/dispatches/${dispatchId}/trucks/${truckId}/pesaje`);
      toast.success('Pesaje eliminado');
      fetchData();
    } catch (error) {
      console.error('Error eliminando pesaje:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar pesaje');
    }
  }, [fetchData]);

  return {
    // Data
    dispatches,
    businesses,
    compras,
    importadoras,
    companies,
    trucks,
    trailers,
    drivers,
    loading,
    
    // UI State
    expandedDispatches,
    isDispatchExpanded,
    modals,
    selectedDispatch,
    selectedTruck,
    isModification,
    
    // Form State
    formData,
    setFormData,
    assignData,
    setAssignData,
    truckData,
    setTruckData,
    editData,
    setEditData,
    justification,
    setJustification,
    dispatchNumberError,
    
    // Actions
    fetchData,
    resetForms,
    openModal,
    closeModal,
    validateDispatchNumber,
    createDispatch,
    assignTruck,
    updateTruckData,
    changeStatus,
    updateDispatch,
    deleteDispatch,
    removeTruck,
    updateTruckAssignment,
    
    // Modal Openers
    openAssignModal,
    openAddTruckDataModal,
    openModifyTruckDataModal,
    openStatusModal,
    openEditDispatchModal,
    openEditTruckAssignmentModal,
    
    // Helpers
    toggleDispatch,
    getImportadoraName,
    getFilteredTrucks,
    getFilteredTrailers,
    getFilteredDrivers,
    
    // Filtros
    filters,
    setFilters,
    filteredDispatches,
    clearFilters,
    hasActiveFilters,
    
    // Reordenamiento
    reorderDispatches,
    reorderTrucks,
    
    // Pesajes de descarga
    uploadPesaje,
    viewPesaje,
    deletePesaje,
    
    // Assignment editing (for ADMIN)
    assignmentData,
    setAssignmentData,
    
    // Role checks
    isTransportista
  };
};

export default useDispatches;
